import React, { useEffect, useMemo, useState } from "react";
import OrderHeader from "./orderHeader";
import ProgressTracker from "./progressTracker";
import OrderSummary from "./orderSummary";
import SelectedServices from "./selectedServices";
import { useLocation, useParams } from "react-router-dom";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { useSelector } from "react-redux";
import axiosInstance from "../../../../configuration/request";
import { capitalizeStatus, formatServiceType } from "../../../../utils/customerHealperFunctions";
import { dateFormatShort, formatDateDDMMYYYY } from "../../../../utils/dateFormat";
import DeliverySection from "./deliverySection";

const totals = {
  platformFee: 0,
  tax: 1,
  deliveryFee: 2,
  total: 28,
};

export default function OrderTracking() {
  const { id } = useParams();
  const location = useLocation();
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const userId = loginDetails.userId || null;
  const [orderDetail, setOrderDetail] = useState(null);
  const [autoOpenStoreChat, setAutoOpenStoreChat] = useState(false);

  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const explicitOrderId = params.get("orderId");
    const explicitChat = params.get("chat");

    // Support malformed links like: /order-tracking?123?chat=store
    const rawSearch = (location.search || "").replace(/^\?/, "");
    const rawFirstToken = rawSearch.split("&")[0];
    const malformedOrderId =
      !explicitOrderId && !id
        ? (rawFirstToken || "").split("?")[0]
        : "";
    const regexChat = rawSearch.match(/(?:^|[?&])chat=([^&]+)/);
    const decodedRegexChat = regexChat?.[1]
      ? decodeURIComponent(regexChat[1])
      : "";

    return {
      orderId: id || explicitOrderId || malformedOrderId || "",
      chat: explicitChat || decodedRegexChat || "",
    };
  }, [id, location.search]);

  useEffect(() => {
    if (deepLink.orderId) {
      fetchOrder(deepLink.orderId);
    }
  }, [deepLink.orderId, userId]);

  useEffect(() => {
    if (deepLink.chat === "store") {
      setAutoOpenStoreChat(true);
    }
  }, [deepLink.chat]);

  const fetchOrder = (orderId) => {
    axiosInstance
      .get(`/customer/orders?user_id=${userId}&order_id=${orderId}`)
      .then((res) => {
        const data = res?.data?.data[0];
        const address = data?.user_address
          ? [
            data.user_address.address_line,
            data.user_address.landmark,
            data.user_address.city,
            data.user_address.state,
            data.user_address.pincode,
          ]
            .filter(Boolean)
            .join(", ")
          : "N/A";
        const mappedOrder = {
          order_id: data?.id,
          order_code: data?.order_id,
          order_date: formatDateDDMMYYYY(data?.created_at),
          storeName: data?.store?.storeName || "",
          storeId: data?.store?.id,
          serviceType: formatServiceType(data?.services?.service_type),
          totalAmount: data?.total_amount || "",
           image: data?.store?.shopImageUrl || null,
          serviceItems:
            data?.services?.items || [],
          estimatedWeightQnt: data?.services?.estimated_weight_or_qty || "",
          storeRating: parseInt(data?.store_avg_rating) || 0,
          customer_feedback_store: data?.feedback,
          deliveryDate: formatDateDDMMYYYY(data?.deliveryDate) || "",
          deliveryPartner: "Amit Singh",
          deliveryAddress: address || "",
          status: data?.status,
          pickups: data?.pickups,
          additionalItems: data?.additionalItems || [],
          tip: data?.tip || 0,
          priceDetails: {
            orderAmount: data?.breakdown?.orderAmount || 0,
            platformFee: data?.breakdown?.platformFee || 0,
            tax: data?.breakdown?.tax || 0,
            deliveryCharge: data?.breakdown?.deliveryCharge || 0,
            discount: data?.breakdown?.discount || 0,
            mismatchPaymentAmount: data?.breakdown?.mismatchPayment || 0,

          },
        };
        setOrderDetail(mappedOrder);
      })
      .catch((error) => {
        showSnackbar(
          error?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  };

  
  return (
    <div className="max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-2">
        Order <span className="text-[#008ECC]">Tracking</span>
      </h2>
      <div className="border-b-3 border-[#008ECC] w-20 mb-6" />

      <div className="w-full border border-[#f8f6f6] bg-white rounded-2xl shadow-[0_0_10px_rgba(0,0,0,0.06)] p-4 sm:p-6">
        <OrderHeader orderDetail={orderDetail} />
        <div className="flex justify-center w-full overflow-x-auto scrollbar-hide">
          <div className="min-w-[700px] lgs:min-w-[800px] lgs:max-w-[8500px]">
            <ProgressTracker status={orderDetail?.status} />
          </div>
        </div>
        <div className="flex gap-4 flex-wrap lgs:flex-nowrap  items-start">
          <div className="flex flex-col w-full lgs:flex-1">
            <OrderSummary
              orderDetail={orderDetail}
              onReviewSubmit={() => fetchOrder(deepLink.orderId)}
              shouldAutoOpenStoreChat={autoOpenStoreChat}
              onAutoOpenStoreChatHandled={() => setAutoOpenStoreChat(false)}
            />
            <div className="w-full">
              {orderDetail?.pickups && orderDetail?.pickups?.map((pickup) => (
                <DeliverySection
                  key={pickup.id || pickup.job_id}
                  pickupDetail={pickup}
                  orderId={orderDetail.order_id}
                  onReviewSubmit={() => fetchOrder(deepLink.orderId)}
                />
              ))}
            </div>
          </div>
          <SelectedServices orderDetail={orderDetail} totals={totals} />
        </div>
      </div>
    </div>
  );
}
